import { useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { FaCheckCircle } from 'react-icons/fa';

import { Button } from '@client/toolkit';

import { PortfolioPaths } from '../../portfolio.router';
import type { OnboardingBuilding } from '../../onboarding/onboarding.types';

interface StepFerdigProps {
  buildings: OnboardingBuilding[];
  createdBuildingIds: string[];
}

/**
 * Completion screen shown after the bulk-create succeeds. Lists the count of
 * created buildings and offers two CTAs:
 *   - jump straight into the first new building's dashboard
 *   - go back to the building list (default for multi-building runs)
 */
export function StepFerdig({ buildings, createdBuildingIds }: StepFerdigProps) {
  const navigate = useNavigate();

  const selectedBuildings = useMemo(() => buildings.filter((b) => b.selected), [buildings]);
  const totalBra = useMemo(
    () => selectedBuildings.reduce((sum, b) => sum + b.bra, 0),
    [selectedBuildings],
  );

  const firstBuildingId = createdBuildingIds[0];
  const goToFirst = () => {
    if (firstBuildingId) {
      navigate(generatePath(PortfolioPaths.Building, { buildingId: firstBuildingId }));
    }
  };

  return (
    <div className="flex flex-col items-center text-center">
      <FaCheckCircle className="text-brand-primary text-6xl mb-6" />

      <h2 className="text-3xl font-bold text-white mb-3">
        {createdBuildingIds.length === 1 ? 'Bygget er opprettet!' : 'Byggene er opprettet!'}
      </h2>
      <p className="text-white/50 mb-10">
        {createdBuildingIds.length} bygg registrert · {totalBra.toLocaleString('nb-NO')} m²
      </p>

      <div className="flex flex-col sm:flex-row gap-3">
        {firstBuildingId && (
          <Button color="primary" size="lg" onClick={goToFirst}>
            {createdBuildingIds.length === 1 ? 'Åpne bygget →' : 'Åpne første bygg →'}
          </Button>
        )}
        <Button
          color="primary-dark"
          size="lg"
          onClick={() => navigate(PortfolioPaths.BuildingList)}
        >
          Gå til byggoversikten
        </Button>
      </div>
    </div>
  );
}
